import SEO from './composants/SEO';
import { SEO_PAGES } from './donnees/seoPages';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useAuth } from './AuthContext';
import './Inscription.css';

// ══════════════════════════════════════════════════════════════
// PEFEP — PAGE INSCRIPTION v3 (Supabase + AuthContext)
// - Mot de passe FORT exigé + indicateur de force
// - Bouton « voir » sur le mot de passe, confirmation à l'aveugle
// - Consentement obligatoire (CGU + Politique de confidentialité)
// ══════════════════════════════════════════════════════════════

function genererCodeParrainage(nom) {
  const nomClean = (nom || 'user')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .substring(0, 8);
  const random = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${nomClean}${random}`;
}

// Indicateur visuel de la robustesse du mot de passe
function IndicateurForce({ mdp }) {
  const criteres = [
    { ok: mdp.length >= 8, label: '8+ caractères' },
    { ok: /[a-z]/.test(mdp), label: 'minuscule' },
    { ok: /[A-Z]/.test(mdp), label: 'majuscule' },
    { ok: /[0-9]/.test(mdp), label: 'chiffre' },
    { ok: /[^A-Za-z0-9]/.test(mdp), label: 'caractère spécial' },
  ];
  const score = criteres.filter(c => c.ok).length;
  const niveau = score <= 2 ? 'faible' : score === 3 ? 'moyen' : score === 4 ? 'bon' : 'fort';
  const couleur = score <= 2 ? '#EF4444' : score === 3 ? '#F59E0B' : score === 4 ? '#00A651' : '#059669';
  return (
    <div className="mdp-force">
      <div className="mdp-force-barre">
        <div className="mdp-force-remplissage" style={{ width: (score / 5 * 100) + '%', background: couleur }}></div>
      </div>
      <div className="mdp-force-texte" style={{ color: couleur }}>Sécurité : {niveau}</div>
      <div className="mdp-force-criteres">
        {criteres.map(c => (
          <span key={c.label} className={c.ok ? 'critere-ok' : 'critere-non'}>{c.ok ? '✓' : '○'} {c.label}</span>
        ))}
      </div>
    </div>
  );
}

export default function Inscription() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [etape, setEtape] = useState(1);
  const [typeCompte, setTypeCompte] = useState(null);

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    ville: '',
    password: '',
    passwordConfirm: '',
  });

  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState(false);
  const [voirMdp, setVoirMdp] = useState(false);
  const [consentement, setConsentement] = useState(false);

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (erreur) setErreur('');
  };

  const choisirTypeCompte = (type) => {
    setTypeCompte(type);
    setEtape(2);
  };

  const validerFormulaire = () => {
    if (!formData.nom.trim()) return 'Le nom est obligatoire';
    if (!formData.email.trim()) return "L'email est obligatoire";
    if (!formData.email.includes('@')) return 'Email invalide';
    if (!formData.telephone.trim()) return 'Le téléphone est obligatoire';
    const mdp = formData.password;
    if (mdp.length < 8) return 'Le mot de passe doit contenir au moins 8 caractères';
    if (!/[a-z]/.test(mdp)) return 'Ajoutez au moins une lettre minuscule';
    if (!/[A-Z]/.test(mdp)) return 'Ajoutez au moins une lettre majuscule';
    if (!/[0-9]/.test(mdp)) return 'Ajoutez au moins un chiffre';
    if (formData.password !== formData.passwordConfirm) return 'Les mots de passe ne correspondent pas';
    if (!consentement) return "Vous devez accepter les Conditions d'utilisation et la Politique de confidentialité";
    return null;
  };

  const handleInscription = async (e) => {
    e.preventDefault();
    setErreur('');

    const validationError = validerFormulaire();
    if (validationError) {
      setErreur(validationError);
      return;
    }

    setChargement(true);

    try {
      // ── ÉTAPE 1 : Créer le compte Supabase Auth ──
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
      });

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('already been registered')) {
          setErreur('Cet email est déjà utilisé. Connectez-vous plutôt.');
        } else if (authError.message.includes('Password')) {
          setErreur('Mot de passe trop faible.');
        } else {
          setErreur('Erreur : ' + authError.message);
        }
        setChargement(false);
        return;
      }

      if (!authData?.user) {
        setErreur('Erreur inattendue lors de la création du compte.');
        setChargement(false);
        return;
      }

      // ── ÉTAPE 2 : Insérer le profil dans la table `clients` ──
      const codeParrainage = genererCodeParrainage(formData.nom);

      const { error: dbError } = await supabase
        .from('clients')
        .insert([
          {
            id: authData.user.id,
            email: formData.email,
            nom: formData.nom,
            prenom: formData.prenom || null,
            telephone: formData.telephone,
            ville: formData.ville || null,
            type_compte: typeCompte,
            plan_dev: typeCompte === 'dev' ? 'starter' : null,
            code_parrainage: codeParrainage,
          },
        ]);

      if (dbError) {
        console.error('Erreur BDD :', dbError);
        setErreur('Erreur de création du profil : ' + dbError.message);
        setChargement(false);
        return;
      }

      // ── ÉTAPE 3 : Charger le profil dans AuthContext ──
      await login(authData.user);

      // ── ÉTAPE 4 : Écran de succès puis redirection ──
      setSucces(true);
      setTimeout(() => {
        if (typeCompte === 'dev') {
          navigate('/developpeur');
        } else {
          navigate('/creer-mon-site');
        }
      }, 2500);

    } catch (err) {
      console.error('Erreur inattendue :', err);
      setErreur('Erreur inattendue : ' + err.message);
      setChargement(false);
    }
  };

  // ══ ÉCRAN DE SUCCÈS ══
  if (succes) {
    return (
      <div className="inscription-container">
        <SEO {...SEO_PAGES.inscription} />
        <div className="inscription-succes">
          <div className="succes-icone">🎉</div>
          <h2>Bienvenue dans l'écosystème PEFEP !</h2>
          <p>Votre compte a été créé avec succès.</p>
          <p className="succes-redirect">
            Redirection vers votre espace {typeCompte === 'dev' ? 'développeur' : 'de création'}...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="inscription-container">
      <SEO {...SEO_PAGES.inscription} />

      {/* PARTIE GAUCHE — Branding PEFEP */}
      <div className="inscription-gauche">
        <div className="inscription-gauche-content">
          <div className="inscription-logo">PEFEP</div>
          <h1>Bienvenue dans l'écosystème PEFEP !</h1>
          <p className="inscription-slogan">Là où vos idées rencontrent la tech</p>

          <div className="inscription-services">
            <div className="service-item">
              <span className="service-icone">💻</span>
              <div>
                <strong>PEFEP Site</strong>
                <span>Sites web professionnels en 30 min</span>
              </div>
            </div>
            <div className="service-item">
              <span className="service-icone">🎓</span>
              <div>
                <strong>PEFEP Académie</strong>
                <span>Formations digital &amp; entrepreneuriat</span>
              </div>
            </div>
            <div className="service-item">
              <span className="service-icone">📣</span>
              <div>
                <strong>Marketing Digital</strong>
                <span>Facebook, Google, TikTok, LinkedIn Ads</span>
              </div>
            </div>
            <div className="service-item">
              <span className="service-icone">🧾</span>
              <div>
                <strong>Faktirasyon</strong>
                <span>Facturation offline-first bientôt disponible</span>
              </div>
            </div>
          </div>

          <div className="inscription-signature">
            <div className="signature-badge">🇭🇹 Made in Haïti</div>
          </div>
        </div>
      </div>

      {/* PARTIE DROITE — Formulaire */}
      <div className="inscription-droite">
        <div className="inscription-droite-content">

          {/* ÉTAPE 1 — Choix du type de compte */}
          {etape === 1 && (
            <>
              <h2>Créer votre compte</h2>
              <p className="inscription-sous-titre">Choisissez le type de compte qui vous correspond</p>

              <div className="type-compte-grid">
                <button
                  type="button"
                  className="type-compte-card"
                  onClick={() => choisirTypeCompte('simple')}
                >
                  <div className="type-compte-icone">👤</div>
                  <h3>Client SIMPLE</h3>
                  <p>Je veux créer <strong>MON site</strong> professionnel</p>
                  <ul>
                    <li>✓ 1 site web à vie</li>
                    <li>✓ Modifications 2/mois</li>
                    <li>✓ Ajouts illimités</li>
                    <li>✓ Support WhatsApp</li>
                  </ul>
                  <div className="type-compte-prix">Dès $49</div>
                </button>

                <button
                  type="button"
                  className="type-compte-card"
                  onClick={() => choisirTypeCompte('dev')}
                >
                  <div className="type-compte-badge">🚀 Pour les pros</div>
                  <div className="type-compte-icone">💼</div>
                  <h3>Compte DEV / Agence</h3>
                  <p>Je crée des sites pour <strong>mes clients</strong></p>
                  <ul>
                    <li>✓ Plusieurs sites par mois</li>
                    <li>✓ Dashboard agence</li>
                    <li>✓ White-label possible</li>
                    <li>✓ Support prioritaire</li>
                  </ul>
                  <div className="type-compte-prix">Dès $49/mois</div>
                </button>
              </div>

              <div className="inscription-lien-connexion">
                Vous avez déjà un compte ? <Link to="/connexion">Se connecter</Link>
              </div>
            </>
          )}

          {/* ÉTAPE 2 — Formulaire */}
          {etape === 2 && (
            <>
              <button type="button" className="btn-retour" onClick={() => setEtape(1)}>
                ← Changer de type de compte
              </button>

              <div className="type-compte-choisi">
                <span className="badge-type">
                  {typeCompte === 'simple' ? '👤 Client SIMPLE' : '💼 Compte DEV / Agence'}
                </span>
              </div>

              <h2>Vos informations</h2>
              <p className="inscription-sous-titre">Remplissez ce formulaire pour créer votre compte</p>

              {erreur && (
                <div className="inscription-erreur">
                  ⚠️ {erreur}
                </div>
              )}

              <form onSubmit={handleInscription} className="inscription-form">
                <div className="form-row">
                  <div className="form-champ">
                    <label>👤 Nom <span className="obligatoire">*</span></label>
                    <input
                      type="text"
                      placeholder="Ex: Presmy"
                      value={formData.nom}
                      onChange={e => updateField('nom', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-champ">
                    <label>👤 Prénom</label>
                    <input
                      type="text"
                      placeholder="Ex: Isaac"
                      value={formData.prenom}
                      onChange={e => updateField('prenom', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-champ">
                  <label>✉ Email <span className="obligatoire">*</span></label>
                  <input
                    type="email"
                    placeholder="exemple@email.com"
                    value={formData.email}
                    onChange={e => updateField('email', e.target.value)}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-champ">
                    <label>📞 Téléphone <span className="obligatoire">*</span></label>
                    <input
                      type="tel"
                      placeholder="+509 3800 0000"
                      value={formData.telephone}
                      onChange={e => updateField('telephone', e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-champ">
                    <label>📍 Ville</label>
                    <input
                      type="text"
                      placeholder="Ex: Léogâne"
                      value={formData.ville}
                      onChange={e => updateField('ville', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-champ">
                  <label>🔒 Mot de passe <span className="obligatoire">*</span></label>
                  <div className="champ-mdp">
                    <input
                      type={voirMdp ? 'text' : 'password'}
                      placeholder="8+ caractères, majuscule et chiffre"
                      value={formData.password}
                      onChange={e => updateField('password', e.target.value)}
                      required
                      minLength={8}
                    />
                    <button type="button" className="btn-voir-mdp" onClick={() => setVoirMdp(!voirMdp)} tabIndex={-1}>
                      {voirMdp ? '🙈 Cacher' : '👁 Voir'}
                    </button>
                  </div>
                  {formData.password && <IndicateurForce mdp={formData.password} />}
                </div>

                <div className="form-champ">
                  <label>🔒 Confirmer le mot de passe <span className="obligatoire">*</span></label>
                  <input
                    type="password"
                    placeholder="Retapez votre mot de passe"
                    value={formData.passwordConfirm}
                    onChange={e => updateField('passwordConfirm', e.target.value)}
                    required
                  />
                </div>

                <label className="inscription-consent">
                  <input type="checkbox" checked={consentement} onChange={e => setConsentement(e.target.checked)} />
                  <span>
                    J'ai lu et j'accepte les <Link to="/conditions-d-utilisation" target="_blank">Conditions d'utilisation</Link> et la <Link to="/politique-de-confidentialite" target="_blank">Politique de confidentialité</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  className="btn-inscription"
                  disabled={chargement || !consentement}
                >
                  {chargement ? '⏳ Création en cours...' : '🚀 Créer mon compte'}
                </button>

                <div className="inscription-mention">
                  <span className="obligatoire">*</span> Champs obligatoires
                </div>
              </form>

              <div className="inscription-lien-connexion">
                Vous avez déjà un compte ? <Link to="/connexion">Se connecter</Link>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}